import React, { useCallback, useState } from 'react';
import { Filter, X, RefreshCw, EyeOff, Target, ChevronDown, Calendar, DollarSign } from 'lucide-react';
import { clsx } from 'clsx';

import { SectionHelpIcon } from '../ui/SectionHelpIcon';
import { useFilterStore } from '../../stores/useFilterStore';
import { useGraphStore } from '../../stores/useGraphStore';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { useStashStore } from '../../stores/useStashStore';
import { TOOLBAR_FOCUS } from './WorkbenchToolbar';
import { workbenchSurfacePopover, workbenchHeaderShell, workbenchInset } from './workbenchStyles';
import { HiddenByTierNotice } from './HiddenByTierNotice';
import { isAllNodesEffective } from '../../utils/graphFilterLogic';

export interface GraphFilters {
    /** Minimum edge value in USD. null = show all (no dust filter). Snap stops:
     *  null / 1 / 10 / 100 / 1000. Forensic-overlay edges, gold-path edges,
     *  and bundle (cluster) edges are exempt from this filter regardless of
     *  threshold — they represent investigative evidence, not noise. */
    dustThresholdUsd: number | null;
    dateFrom: string; // ISO date string
    dateTo: string;
    riskLevels: Set<'low' | 'medium' | 'high' | 'critical'>;
    entityTypes: Set<string>;
    /** Max hop distance from selected node (null = no limit) */
    maxHops: number | null;
    /** Quick-hide presets */
    hideExchanges: boolean;
    showOnlyHighRisk: boolean;
    /** Canvas intelligence tiers (post-investigation) */
    showIntermediateNodes: boolean;
    showAllNodes: boolean;
    /** True once the user has picked an intermediaries / all-nodes mode. Until
     *  then the Tier-3 filter only engages on dense graphs (see
     *  utils/graphFilterLogic.ts TIER_DENSITY_THRESHOLD). */
    tierFilterChosen: boolean;
    /** Show nodes whose risk was never assessed (the "not assessed" chip). */
    showNotAssessed: boolean;
}

export const EMPTY_FILTERS: GraphFilters = {
    dustThresholdUsd: null,
    dateFrom: '',
    dateTo: '',
    riskLevels: new Set(['low', 'medium', 'high', 'critical']),
    entityTypes: new Set(),
    maxHops: null,
    hideExchanges: false,
    showOnlyHighRisk: false,
    showIntermediateNodes: false,
    showAllNodes: false,
    tierFilterChosen: false,
    showNotAssessed: true,
};

interface GraphFilterBarProps {
    /** Override props — all optional, reads from stores by default */
    filters?: GraphFilters;
    onFiltersChange?: (filters: GraphFilters) => void;
    totalEdges?: number;
    visibleEdges?: number;
    totalNodes?: number;
    visibleNodes?: number;
    availableEntityTypes?: string[];
    selectedNodeId?: string;
    onClose?: () => void;
    /** bar = full-width strip below header; popover = dropdown panel anchored in toolbar */
    variant?: 'bar' | 'popover';
}

/** Plain-English labels for entity type chips. */
const ENTITY_LABELS: Record<string, string> = {
    exchange: 'Exchange',
    mixer: 'Mixer',
    bridge: 'Bridge',
    dex: 'DEX',
    scam: 'Scam',
    sanctioned: 'Sanctioned',
    unknown: 'Unknown',
    wallet: 'Wallet',
    contract: 'Contract',
    merchant: 'Merchant',
};

function formatEntityLabel(type: string): string {
    const key = type.toLowerCase().replace(/\s+/g, '_');
    return ENTITY_LABELS[key] ?? type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

interface FilterChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    active?: boolean;
    /** Strikethrough when excluded (entity type filter). */
    excluded?: boolean;
}

/** shadcn-style outline button — idle chips read as pressable controls. */
const FilterChip: React.FC<FilterChipProps> = ({ active, excluded, className, children, ...props }) => (
    <button
        type="button"
        aria-pressed={active}
        className={clsx(
            TOOLBAR_FOCUS,
            'inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors cursor-pointer',
            active
                ? 'border-primary/50 bg-primary/15 text-primary font-semibold hover:bg-primary/20 hover:border-primary/60'
                : excluded
                    ? 'border-border/60 bg-muted/70 text-muted-foreground/45 line-through hover:bg-muted hover:border-border'
                    : 'border-border bg-secondary/90 text-foreground hover:bg-muted/80 hover:border-primary/40',
            className,
        )}
        {...props}
    >
        {children}
    </button>
);

interface FilterSectionProps {
    icon?: React.ReactNode;
    label: string;
    hint?: string;
    children: React.ReactNode;
    className?: string;
    /** Popover uses stacked rows so chip rows get full width and wrap cleanly. */
    stacked?: boolean;
}

const FilterSection: React.FC<FilterSectionProps> = ({ icon, label, hint, children, className, stacked }) => (
    <div className={clsx(stacked ? 'flex flex-col gap-2 min-w-0' : 'flex flex-wrap items-center gap-2.5', className)}>
        <div className={clsx('flex items-center gap-1.5 shrink-0', !stacked && 'min-w-[6rem]')}>
            {icon && <span className="text-foreground/70 [&>svg]:w-4 [&>svg]:h-4">{icon}</span>}
            <span className="text-xs font-semibold text-foreground tracking-tight" title={hint}>{label}</span>
        </div>
        <div className={clsx('flex flex-wrap items-center gap-1.5 min-w-0', stacked && 'w-full')}>{children}</div>
    </div>
);

const utilityBtnClass = clsx(
    TOOLBAR_FOCUS,
    'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-secondary/90 text-foreground transition-colors cursor-pointer hover:bg-muted/80 hover:border-primary/40',
);

export const GraphFilterBar: React.FC<GraphFilterBarProps> = (propOverrides) => {
    const variant = propOverrides.variant ?? 'bar';
    const isPopover = variant === 'popover';
    const storeFilters = useFilterStore(s => s.graphFilters);
    const storeSetFilters = useFilterStore(s => s.setGraphFilters);
    const storeCounts = useFilterStore(s => s.filterCounts);
    const storeElements = useGraphStore(s => s.elements);
    const storeSelectedNodeId = useSelectionStore(s => s.selectedNodeId);

    const filters = propOverrides.filters ?? storeFilters;
    const onFiltersChange = propOverrides.onFiltersChange ?? storeSetFilters;
    const totalEdges = propOverrides.totalEdges ?? storeCounts.totalEdges;
    const visibleEdges = propOverrides.visibleEdges ?? storeCounts.visibleEdges;
    const totalNodes = propOverrides.totalNodes ?? storeCounts.totalNodes;
    const visibleNodes = propOverrides.visibleNodes ?? storeCounts.visibleNodes;

    const availableEntityTypes = propOverrides.availableEntityTypes ?? (() => {
        const types = new Set<string>();
        storeElements.forEach(el => {
            if (!el.data?.source && el.data?.entity_type) types.add(el.data.entity_type);
        });
        return Array.from(types).sort();
    })();
    const selectedNodeId = propOverrides.selectedNodeId ?? storeSelectedNodeId;
    const onClose = propOverrides.onClose ?? (() => useFilterStore.getState().setShowFilterBar(false));
    const isFiltered = visibleEdges < totalEdges || visibleNodes < totalNodes;

    const filterStashedCount = useStashStore(s => {
        let n = 0;
        for (const key in s.entries) {
            if (s.entries[key].reason === 'filter') n++;
        }
        return n;
    });

    // "All nodes" is what the viewer sees unless the user chose a tier mode or
    // the graph is dense enough that cluster folding takes over.
    const allNodesEffective = isAllNodesEffective(filters, totalNodes);

    const [showAdvanced, setShowAdvanced] = useState(!isPopover);

    const showAllSections = isPopover || showAdvanced;

    const update = useCallback((partial: Partial<GraphFilters>) => {
        onFiltersChange({ ...filters, ...partial });
    }, [filters, onFiltersChange]);

    const resetAll = useCallback(() => {
        onFiltersChange({ ...EMPTY_FILTERS, riskLevels: new Set(['low', 'medium', 'high', 'critical']) });
        setShowAdvanced(false);
    }, [onFiltersChange]);

    const toggleRisk = useCallback((level: 'low' | 'medium' | 'high' | 'critical') => {
        const next = new Set(filters.riskLevels);
        if (next.has(level)) {
            next.delete(level);
        } else {
            next.add(level);
        }
        update({ riskLevels: next });
    }, [filters.riskLevels, update]);

    const toggleEntityType = useCallback((type: string) => {
        const next = new Set(filters.entityTypes);
        if (next.has(type)) {
            next.delete(type);
        } else {
            next.add(type);
        }
        update({ entityTypes: next });
    }, [filters.entityTypes, update]);

    const hopReference = selectedNodeId ? 'the selected address' : 'the investigation root';

    const inputClass = clsx(
        TOOLBAR_FOCUS,
        'px-3 py-1.5 bg-secondary/90 border border-border rounded-md text-xs text-foreground min-w-[8.5rem] transition-colors hover:border-primary/40 focus:border-primary/50',
    );

    const filterBody = (
        <div className="flex flex-col gap-3">
            <div className={clsx('rounded-lg px-3 py-3 flex flex-col gap-3', workbenchInset)}>
                <FilterSection
                    stacked={isPopover}
                    icon={<DollarSign />}
                    label="Min. amount"
                    hint="Hide transfers below this USD value. Trace paths and evidence edges are always kept."
                >
                        {([null, 1, 10, 100, 1000] as const).map(v => {
                            const label = v === null ? 'Show all' : v >= 1000 ? `$${v / 1000}k+` : `$${v}+`;
                            return (
                                <FilterChip
                                    key={String(v)}
                                    active={filters.dustThresholdUsd === v}
                                    onClick={() => update({ dustThresholdUsd: v })}
                                    title={
                                        v === null
                                            ? 'Show every transfer, including small amounts'
                                            : `Hide transfers below $${v} (USD equivalent)`
                                    }
                                >
                                    {label}
                                </FilterChip>
                            );
                        })}
                    </FilterSection>

                    <div className="h-px bg-muted-foreground/20" aria-hidden />

                    <FilterSection
                        stacked={isPopover}
                        icon={<EyeOff />}
                        label="Visibility"
                        hint="Quick presets to simplify the canvas view"
                    >
                        <FilterChip
                            active={filters.hideExchanges}
                            onClick={() => update({ hideExchanges: !filters.hideExchanges })}
                            title="Hide known exchange addresses (Binance, WazirX, etc.)"
                        >
                            Hide exchanges
                        </FilterChip>
                        <FilterChip
                            active={filters.showOnlyHighRisk}
                            onClick={() => update({ showOnlyHighRisk: !filters.showOnlyHighRisk })}
                            title="Show only addresses flagged as high or critical risk"
                        >
                            High risk only
                        </FilterChip>
                        <FilterChip
                            active={filters.showIntermediateNodes}
                            onClick={() => update({ showIntermediateNodes: !filters.showIntermediateNodes, showAllNodes: false, tierFilterChosen: true })}
                            title="Show first-hop counterparties that were discovered during tracing"
                        >
                            Show intermediaries
                        </FilterChip>
                        <FilterChip
                            active={allNodesEffective}
                            onClick={() => update({
                                showAllNodes: !allNodesEffective,
                                showIntermediateNodes: true,
                                tierFilterChosen: true,
                            })}
                            title="Show every address discovered during investigation, including pass-through nodes"
                        >
                            Show all discovered
                        </FilterChip>
                    </FilterSection>
                </div>

                {/* Advanced filters — collapsed by default */}
                {showAllSections && (
                    <div className={clsx('rounded-lg px-3 py-3 flex flex-col gap-3 animate-in fade-in slide-in-from-top-1 duration-150', workbenchInset)}>
                        <FilterSection
                            stacked={isPopover}
                            icon={<Calendar />}
                            label="Date range"
                            hint="Only show transfers within this period"
                        >
                            <input
                                type="date"
                                value={filters.dateFrom}
                                onChange={(e) => update({ dateFrom: e.target.value })}
                                className={inputClass}
                                aria-label="From date"
                                title="Earliest on-chain transfer date (block time) to include"
                            />
                            <span className="text-xs text-muted-foreground">to</span>
                            <input
                                type="date"
                                value={filters.dateTo}
                                onChange={(e) => update({ dateTo: e.target.value })}
                                className={inputClass}
                                aria-label="To date"
                                title="Latest on-chain transfer date (block time) to include"
                            />
                        </FilterSection>

                        <div className="h-px bg-muted-foreground/20" aria-hidden />

                        <FilterSection
                            stacked={isPopover}
                            label="Risk level"
                            hint="Toggle which risk scores remain visible. Not assessed = no risk result yet; it is not the same as low."
                        >
                            {([
                                ['low', 'Low'],
                                ['medium', 'Medium'],
                                ['high', 'High'],
                                ['critical', 'Critical'],
                            ] as const).map(([level, label]) => (
                                <FilterChip
                                    key={level}
                                    active={filters.riskLevels.has(level)}
                                    onClick={() => toggleRisk(level)}
                                    title={`${filters.riskLevels.has(level) ? 'Hide' : 'Show'} ${label.toLowerCase()}-risk addresses`}
                                >
                                    {label}
                                </FilterChip>
                            ))}
                            <FilterChip
                                key="not_assessed"
                                data-testid="risk-chip-not-assessed"
                                active={filters.showNotAssessed !== false}
                                onClick={() => update({ showNotAssessed: filters.showNotAssessed === false })}
                                title={`${filters.showNotAssessed === false ? 'Show' : 'Hide'} addresses whose risk was never assessed (no triage result)`}
                            >
                                Not assessed
                            </FilterChip>
                        </FilterSection>

                        {availableEntityTypes.length > 0 && (
                            <>
                                <div className="h-px bg-muted-foreground/20" aria-hidden />
                                <FilterSection
                                    stacked={isPopover}
                                    label="Entity type"
                                    hint="Click to exclude a category; all types shown when none are excluded"
                                >
                                    {availableEntityTypes.map(type => {
                                        const isExcluded = filters.entityTypes.size > 0 && !filters.entityTypes.has(type);
                                        const isWhitelisted = filters.entityTypes.size > 0 && filters.entityTypes.has(type);
                                        return (
                                            <FilterChip
                                                key={type}
                                                active={isWhitelisted}
                                                excluded={isExcluded}
                                                onClick={() => toggleEntityType(type)}
                                                title={
                                                    isExcluded
                                                        ? `Show ${formatEntityLabel(type)} addresses again`
                                                        : `Hide ${formatEntityLabel(type)} addresses`
                                                }
                                            >
                                                {formatEntityLabel(type)}
                                            </FilterChip>
                                        );
                                    })}
                                </FilterSection>
                            </>
                        )}

                        <div className="h-px bg-muted-foreground/20" aria-hidden />

                        <FilterSection
                            stacked={isPopover}
                            icon={<Target />}
                            label="Hop distance"
                            hint={`Limit visible addresses by hops from ${hopReference}`}
                        >
                            {[1, 2, 3, null].map(h => (
                                <FilterChip
                                    key={h ?? 'all'}
                                    active={filters.maxHops === h}
                                    onClick={() => update({ maxHops: h })}
                                    title={
                                        h
                                            ? `Show only addresses within ${h} hop${h > 1 ? 's' : ''} of ${hopReference}`
                                            : 'No hop limit — show all addresses on the canvas'
                                    }
                                >
                                    {h === null ? 'No limit' : `${h} hop${h > 1 ? 's' : ''}`}
                                </FilterChip>
                            ))}
                        </FilterSection>
                    </div>
                )}
        </div>
    );

    return (
        <div
            className={clsx(
                isPopover
                    ? clsx('flex flex-col min-w-0 min-h-0 max-h-full overflow-hidden rounded-xl', workbenchSurfacePopover)
                    : clsx('px-4 py-3 shrink-0 z-10 relative border-b border-border/40', workbenchHeaderShell),
            )}
            data-testid="graph-filter-bar"
        >
            {/* Header */}
            <div className={clsx(
                'shrink-0',
                isPopover ? 'px-3 pt-3 pb-2.5 border-b border-border/40' : 'mb-3',
            )}>
                <div className={clsx('flex items-start justify-between gap-3', !isPopover && 'items-center')}>
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        {!isPopover && (
                            <div className="flex items-center justify-center w-7 h-7 rounded-md bg-secondary border border-muted-foreground/25">
                                <Filter className="w-4 h-4 text-foreground shrink-0" />
                            </div>
                        )}
                        <span className="font-semibold text-foreground text-sm">
                            Graph filters
                        </span>
                        <SectionHelpIcon
                            content="Hide nodes and transfers that do not match your criteria. Matching items stay on the canvas; filtered items move to the stash tray and can be restored by relaxing filters."
                        />
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        {!isPopover && (
                            <button
                                type="button"
                                onClick={() => setShowAdvanced(v => !v)}
                                className={clsx(utilityBtnClass, showAdvanced && 'bg-primary/15 border-primary/50 text-primary font-semibold')}
                                aria-expanded={showAdvanced}
                                aria-pressed={showAdvanced}
                            >
                                {showAdvanced ? 'Fewer filters' : 'More filters'}
                                <ChevronDown className={clsx('w-3.5 h-3.5 transition-transform', showAdvanced && 'rotate-180')} />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={resetAll}
                            className={utilityBtnClass}
                            title="Clear all filters and show everything on the canvas"
                            aria-label="Reset all filters"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Reset{isPopover ? '' : ' all'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className={clsx(utilityBtnClass, 'p-1.5')}
                            title="Close filter panel"
                            aria-label="Close filters"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                {(isFiltered || filterStashedCount > 0) && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <HiddenByTierNotice />
                        {isFiltered && (
                            <span className="px-2 py-0.5 bg-secondary text-foreground rounded-md text-xs font-medium border border-muted-foreground/25">
                                Showing {visibleEdges} of {totalEdges} transfers · {visibleNodes} of {totalNodes} addresses
                            </span>
                        )}
                        {filterStashedCount > 0 && (
                            <span
                                className="px-2 py-0.5 bg-secondary text-foreground rounded-md text-xs font-semibold border border-muted-foreground/25"
                                title="These items were hidden by filters. Relax filters to restore them, or open the stash tray."
                            >
                                {filterStashedCount} hidden
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className={clsx(
                isPopover && 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 pt-3 pb-5 styled-scrollbar page-scrollbar',
            )}>
                {filterBody}
            </div>
        </div>
    );
};
